import type { DimensionMode } from "@v2/features/modifiers/base/DimensionSelector";
import type { DimensionModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import { useComputedElementDimensions } from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import DimensionSelector from "@v2/features/modifiers/base/DimensionSelector";
import { deriveDimensionFromClassName } from "replo-runtime/shared/utils/dimensions";

interface HeightModifierProps {
  onChange: (modifiers: DimensionModifierValue[]) => void;
  excludeOptions?: DimensionMode[];
}

const HeightModifier: React.FC<HeightModifierProps> = ({
  onChange,
  excludeOptions,
}) => {
  const computedElementDimensions = useComputedElementDimensions();
  const extractedHeight = useSemanticModifierValue({
    semanticExtractor: ({ semanticElement, computedStyles }) => {
      const classDerived = deriveDimensionFromClassName(
        semanticElement.cssClasses || undefined,
        "height",
      );
      if (classDerived !== null) {
        return classDerived;
      }
      const heightValue = computedStyles.height;
      if (heightValue === "auto") {
        return "auto" as const;
      }
      if (heightValue === "100%") {
        return "full" as const;
      }
      const match = heightValue?.match(/^(\d+(?:\.\d+)?)/);
      if (match?.[1]) {
        const num = Math.round(Number.parseFloat(match[1]));
        return isNaN(num) ? null : num;
      }
      return null;
    },
  });

  const currentValue = extractedHeight ?? null;

  const handleValueChange = (height: number | "auto" | "full" | null) => {
    if (height !== null) {
      onChange([
        {
          type: "dimension" as const,
          side: "height",
          value: height,
        },
      ]);
    }
  };

  return (
    <div className="height-modifier">
      <DimensionSelector
        type="height"
        value={currentValue}
        onSelect={handleValueChange}
        excludeOptions={excludeOptions}
        computedDimension={computedElementDimensions?.height ?? null}
      />
    </div>
  );
};

export default HeightModifier;
